import { IconButton } from "@material-tailwind/react";
import { TrashIcon, WrenchScrewdriverIcon } from "@heroicons/react/24/outline";
import { useState } from "react";
import toast from "react-hot-toast";
import { t } from "i18next";
import Loading from "../../../../components/loading/Loading";
import EditCreateDrawer from "../../../../components/edit-create-drawer/EditCreateDrawer";
import CustomerForm, { CustomerFormValidator } from "../../../../components/data/customer/CustomerForm";
import { validator } from "../../../../services/form-validator/form-validator";
import { api } from "../../../../services/api/api";
import { showApiErrorToast } from "../../../../services/api/error";
import { saveExternalId } from "../../../../services/odoo/odoo";
import { useDrawerLayoutDispatch } from "../../../../components/drawer-layout/DrawerLayout";
import { useUserRoles } from "../../../../hooks/useUserRoles";
import DeleteCustomerModal from "./modals/DeleteCustomerModal";
import AdminCustomerPatchDrawer from "./AdminCustomerPatchDrawer";
import "../interventions/WorkorderAdminActions.scss";

export default function CustomerAdminActions({
  customer,
  onChange,
  onRemove
}: {
  customer: EasyTech.Customer,
  onChange?: () => void,
  onRemove?: (customer: EasyTech.Customer) => void
}) {
  const [delLoad, setDelLoad] = useState(false);
  const dispatch = useDrawerLayoutDispatch();
  const { isSuperAdmin } = useUserRoles();

  /**
   * La correction partielle est réservée au super-admin : c'est la seule porte qui ouvre les
   * colonnes que le PUT verrouille (`ref`, `favorite`, `isGroup`, `lastWorkOrder`). Masquer le
   * bouton est une politesse, pas une protection — l'API répond 403 de toute façon.
   */
  const openPatchDrawer = () => {
    dispatch({
      type: "open",
      title: t("admin:customers.patch.title"),
      content: <AdminCustomerPatchDrawer customerId={customer.id} onPatched={() => onChange?.()} />
    });
  };

  /**
   * L'identifiant Odoo part en premier, sur son propre verbe : le PUT voisin ne peut pas le
   * déplacer (`CustomerService.merge` recopie la valeur stockée), et un identifiant déjà pris
   * répond 409 avant que quoi que ce soit n'ait été enregistré. Aucun appel quand il n'a pas bougé.
   */
  const updateCustomer = async (data: EasyTech.Customer): Promise<void> => {
    if (!validator.isFormValid(data, CustomerFormValidator)) {
      toast.error(t("common:formError"));
      throw new Error("invalid customer form");
    }

    try {
      await saveExternalId("customers", data.id, customer.externalId, data.externalId);
    } catch (error) {
      showApiErrorToast(error, { fallbackKey: "client:odoo:externalId:updateError" });
      throw error;
    }

    try {
      await api.put(`admin/customers/${data.id}`, data);
    } catch (error) {
      showApiErrorToast(error);
      throw error;
    }

    onChange?.();
    toast.success(t("admin:customers.messages.updateSuccess"));
  };

  return (
    <div
      className="admin-wo-actions"
      data-no-link
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {onRemove ? (
        <IconButton
          onClick={() => onRemove(customer)}
          variant="text"
          className="icon-btn"
          size="sm"
        >
          <TrashIcon className="w-[20px] h-[20px]" />
        </IconButton>
      ) : (
        <>
          {isSuperAdmin && (
            <IconButton
              onClick={openPatchDrawer}
              variant="text"
              className="icon-btn"
              size="sm"
              title={t("admin:customers.patch.title")}
            >
              <WrenchScrewdriverIcon className="w-[20px] h-[20px]" />
            </IconButton>
          )}

          <EditCreateDrawer<EasyTech.Customer>
            data={customer}
            entityName="customer"
            FormComponent={CustomerForm}
            isNotCreatable
            onSubmit={updateCustomer}
            validator={CustomerFormValidator}
          />

          {delLoad === false ? (
            <IconButton
              onClick={() => setDelLoad(true)}
              variant="text"
              className="icon-btn"
              size="sm"
            >
              <TrashIcon className="w-[20px] h-[20px]" />
            </IconButton>
          ) : (
            <Loading size="h-4 w-4" />
          )}

          {delLoad && (
            <DeleteCustomerModal
              open={delLoad}
              customer={customer}
              onDelete={() => onChange?.()}
              onClose={() => setDelLoad(false)}
            />
          )}
        </>
      )}
    </div>
  );
}
